import React, { useState } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import ThemeLogo from "./ThemeLogo";
import tickIcon from "../assets/images/icon-pricing-tick.svg";
import { onAuthStateChanged } from "firebase/auth";
import CountUpOnView from "./CountUpOnView";

const plans = [
  {
    name: "Starter",
    subtitle: "Freelancers / Part-Time",
    price: {
      monthly: 14.99,
      yearly: 161.89,
    },
    priceIds: {
      monthly: process.env.REACT_APP_STRIPE_MONTH_STANDARD_KEY,
      yearly: process.env.REACT_APP_STRIPE_YEARLY_STANDARD_KEY,
    },
    assessments: "30 Assessments per month",
    features: [
      "Complete EPC Spark Tool Suite",
      "Professional Client & Property Management",
      "AI-Powered EPC Assistant",
      "Advanced Heat Loss Analytics",
      "Smart Voice-to-Text Technology",
    ],
    button: "Start Your Free Plan",
  },
  {
    name: "Pro",
    subtitle: "Full-Time Assessors",
    price: {
      monthly: 24.99,
      yearly: 269.89,
    },
    priceIds: {
      monthly: process.env.REACT_APP_STRIPE_MONTH_PRO_KEY,
      yearly: process.env.REACT_APP_STRIPE_YEARLY_PRO_KEY,
    },
    assessments: "60 Assessments per month",
    features: [
      "Complete EPC Spark Tool Suite",
      "Professional Client & Property Management",
      "AI-Powered EPC Assistant",
      "Advanced Heat Loss Analytics",
      "Smart Voice-to-Text Technology",
    ],
    popular: true,
    button: "Get Started",
  },
  {
    name: "Premium",
    subtitle: "High-Volume Professionals",
    price: {
      monthly: 34.99,
      yearly: 377.89,
    },
    priceIds: {
      monthly: process.env.REACT_APP_STRIPE_MONTH_ULTIMATE_KEY,
      yearly: process.env.REACT_APP_STRIPE_YEARLY_ULTIMATE_KEY,
    },
    assessments: "90 Assessments per month",
    features: [
      "Complete EPC Spark Tool Suite",
      "Professional Client & Property Management",
      "AI-Powered EPC Assistant",
      "Advanced Heat Loss Analytics",
      "Smart Voice-to-Text Technology",
    ],
    button: "Get Started",
  },
];

export default function PricingCards({
  buttonText = "Get Started",
  onPlanSelect,
}) {
  const [isYearly, setIsYearly] = useState(false);
  const navigate = useNavigate();

  const handleBuy = (plan) => {
    if (onPlanSelect) {
      onPlanSelect(plan, isYearly);
    } else {
      // Use onAuthStateChanged to ensure user is loaded
      onAuthStateChanged(auth, (currentUser) => {
        if (currentUser) {
          navigate("/payment", {
            state: {
              user: {
                firstName: currentUser.displayName
                  ? currentUser.displayName.split(" ")[0]
                  : "",
                lastName: currentUser.displayName
                  ? currentUser.displayName.split(" ").slice(1).join(" ")
                  : "",
                email: currentUser.email || "",
                phone: currentUser.phoneNumber || "",
                streetAddress: "",
                city: "",
                county: "",
                postCode: "",
              },
              plan,
              isYearly,
            },
          });
        } else {
          navigate("/register");
        }
      });
    }
  };

  return (
    <section className="pricing-section py-5" id="pricing">
      <Container>
        <div className="text-center mb-5">
          <ThemeLogo style={{ height: 120, marginBottom: 16 }} alt="EPC Spark" />
          <h2 className="section-title">Ready to Begin?</h2>
          <div className="pricing-subtitle mb-3">
            Flexible subscription plans designed to scale with your business.
          </div>
          <div className="pricing-toggle d-inline-flex align-items-center my-3">
            <span className={!isYearly ? "text-primary fw-bold" : "text-muted"}>
              Monthly
            </span>
            <div
              className={`toggle-switch mx-2${isYearly ? " yearly" : ""}`}
              onClick={() => setIsYearly(!isYearly)}
            >
              <div className={`toggle-knob${isYearly ? " yearly" : ""}`}></div>
            </div>
            <span className={isYearly ? "text-primary fw-bold" : "text-muted"}>
              Yearly
            </span>
            <span className="save-badge ms-2">Save 10%</span>
          </div>
        </div>
        <Row>
          {plans.map((plan, idx) => {
            return (
              <Col md={4} key={idx} className="mb-4">
                <div className="pricing-card-border h-100 card-aiwave">
                  <div
                    className={`pricing-card h-100${plan.popular ? " popular" : ""}`}
                  >
                    {plan.popular && (
                      <span className="popular-badge">Popular</span>
                    )}
                    <h3 className="plan-name">
                      {plan.name}
                    </h3>
                    <div className="plan-subtitle mb-2">{plan.subtitle}</div>
                    <div className="price mb-2">
                      <span className="currency">£</span>
                      <span className="amount">
                        <CountUpOnView 
                          end={isYearly ? plan.price.yearly : plan.price.monthly}
                          duration={1.5}
                        />
                      </span>
                      <span className="period">/{isYearly ? "yr" : "mo"}</span>
                    </div>
                    {/* Free Trial Message */}
                    <div className="text-center mb-3">
                      <span className="text-primary fw-semibold fs-6">
                        🎉 1 Month Free Trial
                      </span>
                    </div>
                    <div
                      className="assessments mb-2"
                      style={{ color: "var(--primary-500)", fontWeight: 600 }}
                    >
                      {plan.assessments}
                    </div>
                    <ul className="feature-list list-unstyled mb-3">
                      {plan.features.map((feature, featureIdx) => (
                        <li key={featureIdx} className="feature-item mb-2">
                          <div className="feature-content">
                            <div className="feature-icon">
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path
                                  d="M16.667 5L7.5 14.167L3.333 10"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <div className="feature-text">
                              <div className="feature-title">{feature}</div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                    <Button
                      variant="warning"
                      className="w-100 buy-now-btn btn-aiwave-primary"
                      onClick={() => handleBuy(plan)}
                    >
                      {buttonText}
                    </Button>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      </Container>
    </section>
  );
}
